import { call } from '@advanza/api'
import { useCallback, useEffect, useState } from 'react'
import type { GoLiveRequirementsResponse } from './types'

interface GoLiveRequirementsState {
    data: GoLiveRequirementsResponse | null
    isLoading: boolean
    error: string | null
}

const toErrorMessage = (error: unknown, fallback: string): string => {
    if (typeof error === 'string') {
        return error
    }

    const { error: apiError, message } = (error ?? {}) as { error?: string; message?: string }

    return apiError || message || fallback
}

/**
 * The go-live checklist for one provider.
 *
 * Deliberately uncached: admins open this modal while fixing the very things it reports on, so
 * every open has to hit the server again.
 */
export const useGoLiveRequirements = (providerId: number) => {
    const [state, setState] = useState<GoLiveRequirementsState>({
        data: null,
        isLoading: true,
        error: null,
    })

    const reload = useCallback(() => {
        setState((current) => ({ ...current, isLoading: true, error: null }))

        return call(`office/providers/get-go-live-requirements/${providerId}`)
            .then((response: GoLiveRequirementsResponse) => {
                setState({ data: response, isLoading: false, error: null })
            })
            .catch((error: unknown) => {
                setState({
                    data: null,
                    isLoading: false,
                    error: toErrorMessage(error, 'Failed to load the go-live requirements'),
                })
            })
    }, [providerId])

    useEffect(() => {
        reload()
    }, [reload])

    return { ...state, reload }
}

export const enableProfile = (providerId: number): Promise<GoLiveRequirementsResponse> =>
    call(`office/providers/enable-profile/${providerId}`, { json: {} })

export const toEnableProfileError = (error: unknown): string =>
    toErrorMessage(error, 'Failed to take the profile live')

/**
 * Recalculate the Trustoo score.
 *
 * Nothing recalculates an offline provider on a schedule, so the score in the checklist can be
 * months old and block a profile that would pass today.
 *
 * @see Office\Controller\ProvidersController::updateScores()
 */
export const recalculateScore = (providerId: number): Promise<unknown> =>
    call(`office/providers/update-scores/${providerId}`)

export const toRecalculateScoreError = (error: unknown): string =>
    toErrorMessage(error, 'Failed to recalculate the score')
